import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import axios from 'axios';
import {
  AlertTriangle,
  Loader2,
  RotateCcw,
  X
} from 'lucide-react';

import { API } from '../../context/AuthContext';


const EditorAdminProvi = lazy(
  () => import('./EditorAdminProvi')
);


const getErrorMessage = (
  error,
  fallback
) => (
  error?.response?.data?.detail
  || error?.message
  || fallback
);


const overlayStyle = {
  position: 'fixed',
  inset: 0,
  zIndex: 200,
  background: '#0B1026',
  color: '#FFFFFF'
};


const embeddedStyle = {
  position: 'relative',
  width: '100%',
  height: 'calc(100dvh - 126px)',
  minHeight: 680,
  overflow: 'hidden',
  background: '#0B1026',
  color: '#FFFFFF'
};


const centeredStyle = {
  minHeight: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24
};


const AdminVisualEditorLauncher = ({
  embedded = false,
  autoOpen = false,
  onClose,
  onDirtyChange
}) => {
  const [open, setOpen] = useState(autoOpen);
  const [loading, setLoading] =
    useState(false);
  const [error, setError] =
    useState('');
  const [initialData, setInitialData] =
    useState(null);
  const [imageBank, setImageBank] =
    useState([]);
  const [
    versionHistory,
    setVersionHistory
  ] = useState({});
  const [sessionKey, setSessionKey] =
    useState(0);

  const proofRef = useRef(null);
  const mountedRef = useRef(true);
  const instanceOwnerRef = useRef({});

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      proofRef.current = null;

      if (
        typeof window !== 'undefined'
        && window.__PROVI_OPTS__?.__instanceOwner
          === instanceOwnerRef.current
      ) {
        delete window.__PROVI_OPTS__;
      }
    };
  }, []);

  const closeEditor = useCallback(() => {
    proofRef.current = null;

    if (
      typeof window !== 'undefined'
      && window.__PROVI_OPTS__?.__instanceOwner
        === instanceOwnerRef.current
    ) {
      delete window.__PROVI_OPTS__;
    }

    if (mountedRef.current) {
      setOpen(false);
      setError('');
    }

    if (typeof onDirtyChange === 'function') {
      onDirtyChange(false);
    }

    if (typeof onClose === 'function') {
      onClose();
    }
  }, [onClose, onDirtyChange]);

  const loadEditor = useCallback(
    async () => {
      if (!mountedRef.current) return;

      setLoading(true);
      setError('');
      proofRef.current = null;

      try {
        const [
          designResponse,
          imagesResponse
        ] = await Promise.all([
          axios.get(
            `${API}/admin/designs/inicio`,
            {
              withCredentials: true
            }
          ),
          axios.get(
            `${API}/admin/images`,
            {
              withCredentials: true
            }
          )
        ]);

        if (!mountedRef.current) return;

        const savedDesign =
          designResponse.data?.design;

        const hasSavedDesign =
          savedDesign
          && typeof savedDesign === 'object'
          && Object.keys(
            savedDesign
          ).length > 0;

        setInitialData(
          hasSavedDesign
            ? savedDesign
            : null
        );

        const images = Array.isArray(
          imagesResponse.data?.items
        )
          ? imagesResponse.data.items
          : [];

        setImageBank(
          images.map((image) => ({
            id: image.id,
            name:
              image.name
              || image.original_name
              || 'Imagen',
            src: image.src
          }))
        );

        const screenIds = Array.from(
          new Set([
            'inicio',
            'perfil',
            'proveedor',
            ...(
              hasSavedDesign
                ? Object.keys(savedDesign)
                : []
            )
          ])
        );

        const historyResponses =
          await Promise.all(
            screenIds.map(
              async (screenId) => {
                try {
                  const response =
                    await axios.get(
                      (
                        `${API}/admin/designs/`
                        + `${encodeURIComponent(
                          screenId
                        )}/versions`
                      ),
                      {
                        withCredentials:
                          true
                      }
                    );

                  return {
                    screenId,
                    items:
                      Array.isArray(
                        response.data?.items
                      )
                        ? response
                            .data.items
                        : []
                  };
                } catch (historyError) {
                  return {
                    screenId,
                    items: []
                  };
                }
              }
          )
          );

        if (!mountedRef.current) return;

        const groupedVersions = {};

        historyResponses.forEach(
          ({
            screenId,
            items
          }) => {
            groupedVersions[screenId] =
              items.map((item) => ({
                id: item.id,
                version: item.version,
                remote: true,
                ts: item.created_at
                  ? new Date(
                      item.created_at
                    ).toLocaleString(
                      'es-EC'
                    )
                  : (
                      'Fecha no '
                      + 'disponible'
                    ),
                who:
                  item.created_by_email
                  || item.created_by
                  || 'Administrador'
              }));
          }
        );

        setVersionHistory(
          groupedVersions
        );

        setSessionKey(Date.now());
      } catch (requestError) {
        if (mountedRef.current) {
          setError(
            getErrorMessage(
              requestError,
              'No se pudo cargar el editor.'
            )
          );
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    const handleOpen = () => {
      setOpen(true);
    };

    window.addEventListener(
      'provi:open-visual-editor',
      handleOpen
    );

    return () => {
      window.removeEventListener(
        'provi:open-visual-editor',
        handleOpen
      );
    };
  }, []);

  useEffect(() => {
    if (autoOpen) {
      setOpen(true);
    }
  }, [autoOpen]);

  useEffect(() => {
    if (open) {
      loadEditor();
    }
  }, [open, loadEditor]);

  const verifyPassword = useCallback(
    async (password) => {
      proofRef.current = null;

      try {
        const { data } = await axios.post(
          `${API}/admin/verify-password`,
          { password },
          { withCredentials: true }
        );

        if (!data?.ok) {
          window.alert(
            'Contraseña incorrecta.'
          );

          return false;
        }

        proofRef.current =
          data.verification_token;

        return true;
      } catch (requestError) {
        window.alert(
          getErrorMessage(
            requestError,
            'No se pudo verificar '
            + 'la contraseña.'
          )
        );

        return false;
      }
    },
    []
  );

  const saveDesign = useCallback(
    async ({
      screen,
      design,
      imageBank: currentImageBank,
      changelog
    }) => {
      const verificationToken =
        proofRef.current;

      if (!verificationToken) {
        throw new Error(
          'La confirmación de contraseña '
          + 'expiró. Intenta guardar nuevamente.'
        );
      }

      try {
        const { data } = await axios.post(
          `${API}/admin/designs/${screen}`,
          {
            design,
            image_bank:
              currentImageBank || [],
            changelog: changelog || [],
            verification_token:
              verificationToken
          },
          {
            withCredentials: true
          }
        );

        proofRef.current = null;

        return data;
      } catch (requestError) {
        proofRef.current = null;

        throw new Error(
          getErrorMessage(
            requestError,
            'No se pudo guardar el diseño.'
          )
        );
      }
    },
    []
  );

  const uploadImage = useCallback(
    async (file) => {
      const form = new FormData();

      form.append('file', file);
      form.append('name', file.name);

      try {
        const { data } = await axios.post(
          `${API}/admin/images`,
          form,
          {
            withCredentials: true
          }
        );

        const image = data?.image;

        if (!image?.id || !image?.src) {
          throw new Error(
            'El servidor no devolvió '
            + 'una imagen válida.'
          );
        }

        const normalized = {
          id: image.id,
          name:
            image.name
            || image.original_name
            || file.name,
          src: image.src
        };

        if (mountedRef.current) {
          setImageBank((current) =>
            current.find(
              (item) =>
                item.id === normalized.id
                || item.src === normalized.src
            )
              ? current
              : [normalized, ...current]
          );
        }

        return normalized;
      } catch (requestError) {
        throw new Error(
          getErrorMessage(
            requestError,
            'No se pudo subir la imagen.'
          )
        );
      }
    },
    []
  );

  const deleteImages = useCallback(
    async (
      imageIds,
      password
    ) => {
      const deletedIds = [];

      try {
        for (const imageId of imageIds) {
          await axios.delete(
            `${API}/admin/images/${imageId}`,
            {
              withCredentials: true,
              data: { password }
            }
          );

          deletedIds.push(imageId);
        }

        if (mountedRef.current) {
          setImageBank((current) =>
            current.filter(
              (image) =>
                !deletedIds.includes(
                  image.id
                )
            )
          );
        }

        return true;
      } catch (requestError) {
        if (
          deletedIds.length
          && mountedRef.current
        ) {
          setImageBank((current) =>
            current.filter(
              (image) =>
                !deletedIds.includes(
                  image.id
                )
            )
          );
        }

        throw new Error(
          getErrorMessage(
            requestError,
            deletedIds.length
              ? (
                  "Algunas imágenes se "
                  + "eliminaron, pero otra "
                  + "estaba protegida."
                )
              : (
                  "No se pudo eliminar "
                  + "la imagen."
                )
          )
        );
      }
    },
    []
  );

  const restoreDesignVersion =
    useCallback(
      async ({
        screen,
        versionId,
        password
      }) => {
        try {
          const { data } =
            await axios.post(
              (
                `${API}/admin/designs/`
                + `${screen}/versions/`
                + `${versionId}/restore`
              ),
              { password },
              {
                withCredentials: true
              }
            );

          if (!mountedRef.current) {
            return null;
          }

          const restoredImages =
            Array.isArray(
              data?.image_bank
            )
              ? data.image_bank.map(
                  (image) => ({
                    id: image.id,
                    name:
                      image.name
                      || 'Imagen',
                    src: image.src
                  })
                )
              : [];

          const versionsResponse =
            await axios.get(
              (
                `${API}/admin/designs/`
                + `${screen}/versions`
              ),
              {
                withCredentials: true
              }
            );

          const items =
            Array.isArray(
              versionsResponse
                .data?.items
            )
              ? versionsResponse
                  .data.items
              : [];

          const nextHistory = {
            ...versionHistory,
            [screen]: items.map(
              (item) => ({
                id: item.id,
                version: item.version,
                remote: true,
                ts: item.created_at
                  ? new Date(
                      item.created_at
                    ).toLocaleString(
                      'es-EC'
                    )
                  : (
                      'Fecha no '
                      + 'disponible'
                    ),
                who:
                  item.created_by_email
                  || item.created_by
                  || 'Administrador'
              })
            )
          };

          if (!mountedRef.current) {
            return null;
          }

          setInitialData(
            data.design
          );

          setImageBank(
            restoredImages
          );

          setVersionHistory(
            nextHistory
          );

          return {
            design: data.design,
            imageBank:
              restoredImages,
            versionHistory:
              nextHistory
          };
        } catch (requestError) {
          throw new Error(
            getErrorMessage(
              requestError,
              'No se pudo restaurar '
              + 'la versión.'
            )
          );
        }
      },
      [versionHistory]
    );

  const aiChat = useCallback(
    async (
      messages,
      context
    ) => {
      try {
        const { data } = await axios.post(
          `${API}/admin/ai/chat`,
          {
            messages,
            context
          },
          {
            withCredentials: true,
            timeout: 190000
          }
        );

        return data?.reply
          || 'La IA no devolvió texto.';
      } catch (requestError) {
        throw new Error(
          getErrorMessage(
            requestError,
            'La IA local no pudo responder.'
          )
        );
      }
    },
    []
  );

  const options = useMemo(
    () => ({
      initialData:
        initialData || undefined,
      imageBank,
      versionHistory,
      verifyPassword,
      onSave: saveDesign,
      uploadImage,
      deleteImages,
      restoreVersion:
        restoreDesignVersion,
      onClose: closeEditor,
      onDirtyChange,
      embedded,
      __instanceOwner:
        instanceOwnerRef.current,
      ai: {
        chat: aiChat
      }
    }),
    [
      initialData,
      imageBank,
      versionHistory,
      verifyPassword,
      saveDesign,
      uploadImage,
      deleteImages,
      restoreDesignVersion,
      closeEditor,
      onDirtyChange,
      embedded,
      aiChat
    ]
  );

  if (!open) {
    return null;
  }

  const containerStyle = embedded
    ? embeddedStyle
    : overlayStyle;

  if (loading) {
    return (
      <div style={containerStyle} data-testid={embedded ? 'visual-editor-embedded' : 'visual-editor-overlay'}>
        <div style={centeredStyle}>
          <div
            style={{
              textAlign: 'center'
            }}
          >
            <Loader2
              size={38}
              className="animate-spin"
            />

            <h2>
              Preparando Estudio visual PROVI
            </h2>

            <p
              style={{
                color: '#B6B0C9'
              }}
            >
              Cargando diseños, imágenes y
              conexión local con Ollama…
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle} data-testid={embedded ? 'visual-editor-embedded' : 'visual-editor-overlay'}>
        <div style={centeredStyle}>
          <section
            style={{
              width: 'min(520px, 100%)',
              padding: 28,
              borderRadius: 18,
              background: '#17112D',
              border:
                '1px solid rgba(255,255,255,.12)'
            }}
          >
            <AlertTriangle
              size={34}
              color="#F59E0B"
            />

            <h2>
              No se pudo abrir el editor
            </h2>

            <p
              style={{
                color: '#D1CBE5'
              }}
            >
              {error}
            </p>

            <div
              style={{
                display: 'flex',
                gap: 10,
                marginTop: 20
              }}
            >
              <button
                type="button"
                onClick={loadEditor}
              >
                <RotateCcw size={16} />
                Reintentar
              </button>

              <button
                type="button"
                onClick={closeEditor}
              >
                <X size={16} />
                Cerrar
              </button>
            </div>
          </section>
        </div>
      </div>
    );
  }

  if (
    typeof window !== 'undefined'
  ) {
    window.__PROVI_OPTS__ = options;
  }

  return (
    <div style={containerStyle} data-testid={embedded ? 'visual-editor-embedded' : 'visual-editor-overlay'}>
      <Suspense
        fallback={
          <div style={centeredStyle}>
            <div
              style={{
                textAlign: 'center'
              }}
            >
              <Loader2
                size={38}
                className="animate-spin"
              />

              <h2>
                Cargando editor visual…
              </h2>
            </div>
          </div>
        }
      >
        <EditorAdminProvi
          key={sessionKey}
        />
      </Suspense>
    </div>
  );
};


export default AdminVisualEditorLauncher;
